import React from 'react';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';

export type Zoom = 'day' | 'week';

/**
 * The single time control for the whole report: a segmented День / Неделя
 * switch. No free date picker, no zoom slider — the owner wanted a process,
 * not freedom. Each zoom owns its own ‹ › stepper below this.
 */
export function ZoomSegment({ value, onChange, compact = false }: { value: Zoom; onChange: (z: Zoom) => void; compact?: boolean }) {
  const { t } = useT();
  const opts: { z: Zoom; full: string; short: string; tiny: string }[] = [
    { z: 'day', full: t('report.zoom.day'), short: t('report.zoom.dayShort'), tiny: t('report.zoom.dayTiny') },
    { z: 'week', full: t('report.zoom.week'), short: t('report.zoom.weekShort'), tiny: t('report.zoom.weekTiny') },
  ];
  return (
    <div className="flex justify-center">
      <div className="inline-flex rounded-lg bg-muted p-0.5">
        {opts.map((o) => (
          <button
            key={o.z}
            type="button"
            onClick={() => onChange(o.z)}
            className={cn(
              compact
                ? 'rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors'
                : 'rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors min-[410px]:px-4',
              value === o.z
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {/* Progressive truncation for the mobile sub-header, where this
                sits shoulder-to-shoulder with the date stepper and a fixed
                "День/Неделя" was the #1 cause of the row's horizontal
                scroll. Exactly one tier shows at a time, picked by viewport
                width (not just "always short" — desktop/wide phones still
                get the full word). */}
            {compact ? (
              // Мобильная полоса: короткие подписи всегда, чтобы рядом с датами
              // не возникало горизонтальной прокрутки.
              <>
                <span className="hidden min-[360px]:inline">{o.short}</span>
                <span className="inline min-[360px]:hidden">{o.tiny}</span>
              </>
            ) : (
              <>
                <span className="hidden min-[410px]:inline">{o.full}</span>
                <span className="hidden min-[360px]:inline min-[410px]:hidden">{o.short}</span>
                <span className="inline min-[360px]:hidden">{o.tiny}</span>
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
